import { lazy, Suspense, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import ApplicationHeader from '@rio-cloud/rio-uikit/ApplicationHeader';
import VolkswagenApplicationHeader from '@rio-cloud/rio-uikit/VolkswagenApplicationHeader';
import NotificationsContainer from '@rio-cloud/rio-uikit/NotificationsContainer';
import { isMobile, isMobileScreen, toggleZoomOnMobile } from '@rio-cloud/rio-uikit/DeviceUtils';

import packageJson from '../../package.json';
import { getThemeFromDocument, STYLE_RIO, STYLE_RIO_LIGHT, Themes } from './header/Themes';
import { Loading } from './components/Loading';
import { ApplicationLabel } from './header/ApplicationLabel';
import { VersionSwitcher } from './header/VersionSwitcher';
import { navItems } from './header/Navigation';
import { MenuToggle } from './header/MenuToggle';
import { UikitContext } from './UikitContext';
import { getStartMenuItems } from './sites/start/StartMenu';
import { getComponentsMenuItems } from './sites/components/ComponentsMenu';
import { getFoundationsMenuItems } from './sites/foundations/FoundationsMenu';
import { getUiTemplatesMenuItems } from './sites/templates/UITemplatesMenu';
import { getUtilitiesMenuItems } from './sites/utilities/UtilitiesMenu';
import { getInternalMenuItems } from './sites/internal/InternalMenu';
import DetachedPreview from './components/playground/DetachedPreview';
import { routes } from './routes';

import './uikit.scss';

const Start = lazy(() => import('./sites/start/Start'));
const Foundations = lazy(() => import('./sites/foundations/Foundations'));
const Components = lazy(() => import('./sites/components/Components'));
const UITemplates = lazy(() => import('./sites/templates/UITemplates'));
const Utilities = lazy(() => import('./sites/utilities/Utilities'));
const DemoService = lazy(() => import('./sites/demoService/index'));
const Internal = lazy(() => import('./sites/internal/Internal'));
const WidgetDemo = lazy(() => import('./sites/widgets/WidgetDemo'));
const WidgetDemoDialog = lazy(() => import('./sites/widgets/WidgetDemoDialog'));
const PostMessageExample = lazy(() => import('./sites/widgets/PostMessageExample'));

const CDN_URL = 'https://uikit.developers.rio.cloud';

const toggleTheme = (style = '', href?: string) => {
    const html = document.documentElement;

    // Remove any theme css before applying a new theme
    const headerLinks = [...document.head.querySelectorAll('link')];
    headerLinks.forEach(headerLink => {
        if (
            headerLink.href.includes('vw-') ||
            headerLink.href.includes('man-') ||
            headerLink.href.includes('scania-') ||
            headerLink.href.includes('traton-')
        ) {
            document.head.removeChild(headerLink);
        }
    });

    // Remove the theme class name
    html.classList.remove('style-vw');
    html.classList.remove('style-man');
    html.classList.remove('style-scania');
    html.classList.remove('style-traton');

    if (style === 'RIO') {
        return;
    }

    // Add the theme CSS import
    const styleName = style.toLowerCase();

    const isDev = import.meta.env.DEV;

    const source = isDev ? 'dist' : CDN_URL;
    const linkRef = href || `${source}/${packageJson.version}/${styleName}-uikit.css`;

    const link = document.createElement('link');
    link.href = linkRef;
    link.type = 'text/css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    // Add the theme class name
    html.classList.add(`style-${style.toLowerCase()}`);
};

const isMenuToggleClicked = (event: MouseEvent | TouchEvent) => {
    const target = event.target as HTMLElement;

    const className = target?.className;
    if (typeof className === 'object') {
        return true;
    }

    if (className.includes('UikitMenuToggle')) {
        return true;
    }

    if (className.includes('ActionBarItemIcon')) {
        return (target.firstChild as HTMLElement).className.includes('UikitMenuToggle');
    }
};

const excludeHeaderUrls = [
    'demoService/start',
    'demoService/table',
    'iframe-widget-example',
    'widgetDemo',
    'widgetDemoDialog',
    'postMessageExample',
    'detached',
];

export const UIKIT = () => {
    const { search, pathname } = useLocation();

    const getInitialTheme = () => {
        const documentTheme = getThemeFromDocument();
        return documentTheme ? `RIO_${documentTheme.toUpperCase()}` : STYLE_RIO_LIGHT;
    };

    const isMobileOrSmallScreen = isMobile() || isMobileScreen();

    const [theme, setTheme] = useState(getInitialTheme());
    const [showHomeIcon, setShowHomeIcon] = useState(false);
    const [showMenu, setShowMenu] = useState(!isMobileOrSmallScreen);
    const [showVolkswagenHeader, setShowVolkswagenHeader] = useState(false);

    useEffect(() => {
        toggleZoomOnMobile();
    }, []);

    const handleMenuIconClicked = () => {
        if (showMenu) {
            setShowMenu(false);
        } else {
            setShowMenu(true);
        }
    };

    const handleMenuOutsideClicked = (event: MouseEvent | TouchEvent) => {
        const isMenuIcon = isMenuToggleClicked(event);
        if (!isMenuIcon && showMenu) {
            setShowMenu(false);
        }
    };

    // Workaround:
    // Replace the encoded # character for deep-links like to the writing guide.
    // This might happen when opening the link from Slack or other applications.
    if (pathname.includes('%23')) {
        window.location.href = window.location.href.replace('%23', '#');
    }

    const searchParams = new URLSearchParams(search?.replace('?', ''));

    useEffect(() => {
        const style = searchParams.get('style');
        const css = searchParams.get('css');
        if (style) {
            toggleTheme(style, css || undefined);
            setShowHomeIcon(style === 'man');
            setShowVolkswagenHeader(style === 'vw');
        }
    }, [searchParams]);

    const handleThemeChange = (themeWithMode: string) => {
        const newTheme = themeWithMode.startsWith(STYLE_RIO) ? themeWithMode.split('-')[0] : themeWithMode;
        toggleTheme(newTheme);
        setShowHomeIcon(newTheme === 'MAN');
        setShowVolkswagenHeader(newTheme === 'VW');

        // Store brand style + light/dark theme in context to be used to switch
        // intro image composition
        setTheme(themeWithMode);
    };

    // Don't show the UIKIT ApplicationHeader for certain deep links
    const hasHeader = !excludeHeaderUrls.includes(pathname.replace('/', ''));

    const actionBarItems = [];
    actionBarItems.push(<VersionSwitcher key='versionSwitcher' />);
    // actionBarItems.push(<About key='about' />);
    actionBarItems.push(<Themes key='themes' onThemeChange={handleThemeChange} />);
    actionBarItems.push(<MenuToggle key='menuToggle' />);

    return (
        <UikitContext.Provider
            value={{
                theme,
                showMenu,
                onMenuIconClicked: handleMenuIconClicked,
                onMenuOutsideClicked: handleMenuOutsideClicked,
                startMenu: getStartMenuItems(),
                foundationsMenu: getFoundationsMenuItems(),
                componentsMenu: getComponentsMenuItems(),
                uiTemplateMenu: getUiTemplatesMenuItems(),
                utilitiesMenu: getUtilitiesMenuItems(),
                internalMenu: getInternalMenuItems(),
            }}
        >
            <ApplicationLayout className='uikit-demo'>
                <NotificationsContainer />
                {hasHeader && (
                    <ApplicationLayout.Header>
                        {showVolkswagenHeader && (
                            <VolkswagenApplicationHeader
                                homeRoute={<NavLink to='/' />}
                                navItems={navItems}
                                actionBarItems={actionBarItems}
                            />
                        )}
                        {!showVolkswagenHeader && (
                            <ApplicationHeader
                                label={<ApplicationLabel />}
                                homeRoute={<NavLink to='/' />}
                                showHomeIcon={showHomeIcon}
                                navItems={navItems}
                                actionBarItems={actionBarItems}
                            />
                        )}
                    </ApplicationLayout.Header>
                )}
                <Suspense fallback={<Loading />}>
                    <Routes>
                        {/* Fallback for external links to icons */}
                        <Route path='/components/icons' element={<Navigate to='/foundations/rioglyph' replace />} />

                        <Route path='detached' element={<DetachedPreview />} />
                        <Route path='start/*' element={<Start />} />
                        <Route path='foundations/*' element={<Foundations />} />
                        <Route path='components/*' element={<Components />} />
                        <Route path='utilities/*' element={<Utilities />} />
                        <Route path='templates/*' element={<UITemplates />} />
                        <Route path='demoService/*' element={<DemoService />} />
                        <Route path={`${routes.INTERNAL}/*`} element={<Internal />} />
                        <Route path='widgetDemo' element={<WidgetDemo />} />
                        <Route path='widgetDemoDialog' element={<WidgetDemoDialog />} />
                        <Route path='postMessageExample' element={<PostMessageExample />} />
                        <Route path='*' element={<Navigate to={`/${routes.START}`} replace />} />
                    </Routes>
                </Suspense>
            </ApplicationLayout>
        </UikitContext.Provider>
    );
};
